import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { resources } from "@constants/hardcoded-strings";
import { ChangeEvent, useEffect } from "react";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { LabelWidthTypes } from "@enums/label-width-types";
import Button from "react-bootstrap/Button";
import styles from "./organization-user-details-admin-api-access-tab.less";
import { OrganizationUserDetailsAdminApiAccessValidationErrors } from "@idw-types/common/validation/organization/organization-user-details/organization-user-details-admin-api-access-validation-errors";

type OrganizationUserDetailsAdminApiAccessTabProps = {
    isDisabled: boolean;
    initialAxnAdminUser: AxnAdminUser;
    axnAdminUserState: AxnAdminUser;
    isApiPasswordSet: boolean;
    isApiAccessEnabled: boolean;
    validationErrors: OrganizationUserDetailsAdminApiAccessValidationErrors;
    setIsApiPasswordSet: (isApiPasswordSet: boolean) => void;
    setIsApiAccessEnabled: (isApiAccessEnabled: boolean) => void;
    setAxnAdminUserState: (axnAdminUser: AxnAdminUser) => void;
}

export const OrganizationUserDetailsAdminApiAccessTab = (props: OrganizationUserDetailsAdminApiAccessTabProps) => {
    const { isDisabled, initialAxnAdminUser, axnAdminUserState, isApiPasswordSet,
        isApiAccessEnabled, validationErrors, 
        setIsApiPasswordSet, setIsApiAccessEnabled,
        setAxnAdminUserState } = props;
    const { axnAdminUserDetails: axnAdminUserDetailsState } = axnAdminUserState;
    const { axnAdminUserDetails: initialAxnAdminUserDetails  } = initialAxnAdminUser;

    const updateApiAccessState = () => {
        setIsApiPasswordSet(initialAxnAdminUserDetails.apiUserName !== null);
        setIsApiAccessEnabled(initialAxnAdminUserDetails.apiUserName !== null);
    }

    const handleApiAccessChange = () => {
        const { apiUserName } = axnAdminUserDetailsState;
        const initialApiUserName = initialAxnAdminUserDetails.apiUserName;
        const finalApiUserName = apiUserName !== null ? null : initialApiUserName || '';

        setIsApiPasswordSet(initialApiUserName !== null && !isApiAccessEnabled);
        setIsApiAccessEnabled(!isApiAccessEnabled);

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetailsState,
                apiUserName: finalApiUserName,
                apiPassword: null
            }
        });
    }

    const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetailsState,
                apiUserName: event.target.value
            }
        });
    }

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetailsState,
                apiPassword: event.target.value
            }
        });
    }

    const onResetOrCancelPasswordClick = () => {
        setIsApiPasswordSet(!isApiPasswordSet);
        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserDetails: {
                ...axnAdminUserDetailsState,
                apiPassword: null
            }
        });
    }

    useEffect(() => {
        if (initialAxnAdminUser) {
            updateApiAccessState();
        }
    }, [initialAxnAdminUser]);

    const adminApiAccessResources = resources.pages.organizationUserDetails.tabs.adminApiAccess;

    return <div className={styles.organizationUserDetailsAdminApiAccessTab}>
        <ContentPageHeader title={adminApiAccessResources.title} />
        <ContentPageBody isMaxWidth={true}>
            <div>
                <ToggleFormGroup
                    controlId="organization-user-api-access-toggle"
                    automationId="organization-user-api-access-toggle"
                    label={adminApiAccessResources.inputs.apiAccess.label}
                    ariaLabel={adminApiAccessResources.inputs.apiAccess.label}
                    labelWidth={LabelWidthTypes.SMALL}
                    disabled={isDisabled || initialAxnAdminUserDetails.apiUserName !== null}
                    isRequired={true}
                    onChange={handleApiAccessChange}
                    checked={isApiAccessEnabled}
                    afterInputLabel={isApiAccessEnabled ? resources.common.active : resources.common.inactive}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    value={`${isApiAccessEnabled}`}
                />

                <InputFormGroup
                    controlId="organization-user-api-access-username-input"
                    automationId={"organization-user-api-access-username-input"}
                    label={adminApiAccessResources.inputs.username.label}
                    ariaLabel={adminApiAccessResources.inputs.username.label}
                    value={axnAdminUserDetailsState.apiUserName || ''}
                    disabled={isDisabled || !isApiAccessEnabled}
                    isRequired={true}
                    labelWidth={LabelWidthTypes.SMALL}
                    placeholder={''}
                    onChange={handleUsernameChange}
                />
                {validationErrors.apiUserNameError}

                <div className={styles.row}>
                    <InputFormGroup
                        controlId="organization-user-api-access-password-input"
                        automationId={"organization-user-api-access-password-input"}
                        className={styles.input}
                        label={adminApiAccessResources.inputs.password.label}
                        ariaLabel={adminApiAccessResources.inputs.password.label}
                        value={isApiPasswordSet ? "XXXXXXXXXXXXXX" : axnAdminUserDetailsState.apiPassword || ''}
                        disabled={isDisabled || isApiPasswordSet || axnAdminUserDetailsState.apiUserName === null}
                        isRequired={!isApiPasswordSet}
                        labelWidth={LabelWidthTypes.SMALL}
                        placeholder={''}
                        onChange={handlePasswordChange}
                    />

                    {(!isDisabled && initialAxnAdminUserDetails.apiUserName !== null) &&
                        <Button
                            id="organization-user-summary-clear-or-cancel-reset-password-button"
                            onClick={onResetOrCancelPasswordClick}
                            variant={"outline-secondary"}>{isApiPasswordSet ? "Reset" : "Cancel"}</Button>
                    }
                </div>

                {validationErrors.apiPasswordError}

            </div>
        </ContentPageBody>
    </div>
}